import { useQueries } from "@tanstack/react-query";
import { ClientsQueryKeys } from "@/api/clients/clients.queries";
import axiosInstance from "@/lib/axiosInstance";
import { getQueryParamsWithCustomQueryObj } from "@/utils/getQueryParamsFromCustomQueryObject";
import type {
  BrandDropdownItem,
  ClientDropdownItem,
} from "@/hooks/work-order/UseWorkOrderDropdowns";
import type { NamedItem } from "./scopeTargets";

const LOOKUP_LIMIT = 100;
const LOOKUP_STALE_TIME_MS = 5 * 60 * 1000;

const fetchClients = async (
  tenantId: string,
  search: string,
  signal: AbortSignal,
): Promise<NamedItem[]> => {
  const params: [string, string][] = [
    ["search", search],
    ["type", "Clients"],
    ["field", "businessName"],
    ["limit", String(LOOKUP_LIMIT)],
    ["filterBy", "tenantId"],
    ["filterValue", tenantId],
  ];
  const query = new URLSearchParams(getQueryParamsWithCustomQueryObj(params)).toString();
  const response = await axiosInstance.get<{ data: ClientDropdownItem[] }>(`/dropdown?${query}`, {
    signal,
  });
  return (response.data.data ?? []).map((client) => ({
    id: client.id,
    label: client.businessName,
  }));
};

const fetchBrands = async (
  clientId: string,
  search: string,
  signal: AbortSignal,
): Promise<NamedItem[]> => {
  const response = await axiosInstance.get<{ data: BrandDropdownItem[] }>(
    `/client/brands/${clientId}/dropdown?search=${encodeURIComponent(search)}&limit=${LOOKUP_LIMIT}`,
    { signal },
  );
  return (response.data.data ?? []).map((brand) => ({ id: brand.id, label: brand.name }));
};

const toLookup = (parentIds: string[], results: { data?: NamedItem[]; isLoading: boolean }[]) => ({
  itemsByParent: Object.fromEntries(parentIds.map((id, index) => [id, results[index].data ?? []])),
  isLoading: results.some((result) => result.isLoading),
});

export function useScopeClients(tenantIds: string[], enabled: boolean, search = "") {
  return useQueries({
    queries: tenantIds.map((tenantId) => ({
      queryKey: [ClientsQueryKeys.dropdown, "scopePicker", tenantId, search],
      queryFn: ({ signal }: { signal: AbortSignal }) => fetchClients(tenantId, search, signal),
      enabled,
      staleTime: LOOKUP_STALE_TIME_MS,
    })),
    combine: (results) => toLookup(tenantIds, results),
  });
}

export function useScopeBrands(clientIds: string[], enabled: boolean, search = "") {
  return useQueries({
    queries: clientIds.map((clientId) => ({
      queryKey: ["brands-dropdown", "scopePicker", clientId, search],
      queryFn: ({ signal }: { signal: AbortSignal }) => fetchBrands(clientId, search, signal),
      enabled,
      staleTime: LOOKUP_STALE_TIME_MS,
    })),
    combine: (results) => toLookup(clientIds, results),
  });
}
